import React from 'react';
import { Type } from '@spotify-internal/encore-creator-web';
import {
  screenSmMin,
  screenMdMin,
  screenLgMin,
  screenXlMin,
  spacer24,
  spacer48,
} from '@spotify-internal/encore-foundation';
import { Helmet } from 'react-helmet';
import { trackEvent } from '../../lib/Tracking';
import styled from 'styled-components';
import { AllTimeStats } from './components/AllTimeStats';
import { TopEpisodes } from './components/TopEpisodes';
import { WrappedCard } from './components/WrappedCard';
import { useMe } from '../../hooks/useMe';
import { getUserType, isEmployee } from '../../lib/User';
import { UserType, MeShows } from '../../types';
import { Redirect } from 'react-router-dom';
import { getUserShows, logger } from '../../lib/Api';
import { CardsSection } from './components/CardsSection';
import { FormattedMessage } from 'react-intl';
import { isS4PodShowWrappedBannersEnabled } from '../../lib/Features';

type Props = {
  networkId: string | null;
};

export function Home(props: Props) {
  const me = useMe();
  const [userShows, setUserShows] = React.useState<MeShows[] | null>(null);

  /**
   * If a God Mode user refreshes the page on /home, we need to redirect them to /gateway.
   */
  const isGodModeUser = props.networkId === null && isEmployee(me!);
  const isWrappedEnabled = isS4PodShowWrappedBannersEnabled(me!.features);

  React.useEffect(() => {
    try {
      logger.logHomePageLoad();
    } catch {}

    trackEvent({ category: 'page-type', action: 'view', label: 'home' });
  }, []);

  React.useEffect(() => {
    async function fetchData() {
      if (getUserType(me!) === UserType.podcast) {
        const { shows } = await getUserShows({
          start: me!.dataRange.from,
          end: me!.dataRange.until,
        });

        setUserShows(shows);
      }
    }

    if (!isGodModeUser) {
      fetchData();
    }
  }, [me]);

  if (isGodModeUser) {
    return <Redirect to="/gateway" />;
  }

  return (
    <>
      <Helmet title="Home" />
      <Type as="h1" variant={Type.heading1} data-testid="home-page">
        <FormattedMessage
          id="home.title"
          description="Title of the home page"
          defaultMessage="Home"
        />
      </Type>
      <GridContainer>
        <section>
          {isWrappedEnabled && <WrappedCard />}
          <CardsSection networkId={props.networkId} />
        </section>
        <StatsSection>
          <AllTimeStats shows={userShows} networkId={props.networkId} />
          <TopEpisodes shows={userShows} networkId={props.networkId} />
        </StatsSection>
      </GridContainer>
    </>
  );
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: 140px;

  grid-template-areas:
    'stats'
    'cards';

  @media (min-width: ${screenSmMin}) {
    grid-column-gap: ${spacer24};
    grid-template-areas: 'cards stats';
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: ${screenMdMin}) {
    grid-column-gap: ${spacer48};
    grid-template-areas: 'cards stats';
    grid-template-columns: 7fr 3fr;
  }

  @media (min-width: ${screenLgMin}) {
    grid-column-gap: ${spacer48};
    grid-template-areas: 'cards stats';
    grid-template-columns: 3fr 1fr;
  }

  @media (min-width: ${screenXlMin}) {
    grid-column-gap: ${spacer48};
    grid-template-areas: 'cards stats';
    grid-template-columns: 4fr 1fr;
  }
`;

const StatsSection = styled.div`
  grid-area: stats;

  > *:not(:last-child) {
    margin-bottom: ${spacer48};
  }
`;
